import React, { useEffect, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import './ActivityToast.scss';

const JAAKKO_AVATAR = `${process.env.PUBLIC_URL}/assets/jaakko_avatar.png`;

/** Time on screen before the bubble dismisses itself. Matches the 7 s the
 *  repo's previous toast used, plus a beat: this one is tappable, so it has to
 *  be noticed and acted on, not just read. */
const VISIBLE_MS = 8000;

type ActivityToastProps = {
  /** Matching listings behind the created interest. 0 renders the
   *  "we'll show homes here" variant instead of a count. */
  count: number;
  /** Open the Activity view (the tab the bubble points at). */
  onOpen: () => void;
  /** Retire the toast for good — it has now been seen. */
  onDismiss: () => void;
};

/**
 * Success bubble shown after creating a buying interest (JAA-315). A violet
 * speech bubble rendered inside the bottom nav, its tail pointing at the
 * Activity tab (anchored via the nav's --jaakko-nav-activity-x variable).
 * Tapping it opens Activity.
 *
 * The countdown starts on mount, which is also the moment the toast becomes
 * visible: AppContent only renders it while the nav is up, and holds the
 * pending toast in storage until then, so a toast owed from the building page
 * waits for the user rather than expiring unseen.
 */
const ActivityToast: React.FC<ActivityToastProps> = ({
  count,
  onOpen,
  onDismiss,
}) => {
  const { t } = useTranslation();

  // Arm once on mount; the ref shields the timer from parent re-renders
  // handing down a new callback identity, which would restart the countdown.
  // Synced in an effect rather than during render: a render React discards
  // would otherwise leave the live timer holding a callback from it.
  const onDismissRef = useRef(onDismiss);
  useEffect(() => {
    onDismissRef.current = onDismiss;
  }, [onDismiss]);
  useEffect(() => {
    const id = window.setTimeout(() => onDismissRef.current(), VISIBLE_MS);
    return () => window.clearTimeout(id);
  }, []);

  return (
    <button type="button" className="activity-toast" onClick={onOpen}>
      <img className="activity-toast__avatar" src={JAAKKO_AVATAR} alt="" />
      <span className="activity-toast__text" role="status">
        {count > 0 ? (
          <Trans
            i18nKey="newLandingPage.activityToast.found"
            count={count}
            components={{ 1: <strong /> }}
          />
        ) : (
          t('newLandingPage.activityToast.empty')
        )}
      </span>
    </button>
  );
};

export default ActivityToast;
